import { v4 as uuidv4 } from 'uuid';

import {
  QuestionSet,
  ImagesQuestion,
  GeographicLocationQuestion,
  SelectByFeatureQuestion,
  SignatureQuestion,
  MarkdownQuestion,
} from './Question';

const STANDARD_FORM_TYPES = {
  PARK_LOG_FORM: 'PARK_LOG_FORM',
  JUMP_MEASUREMENT_FORM: 'JUMP_MEASUREMENT_FORM',
  POST_INCIDENT_INSPECTION_FORM: 'POST_INCIDENT_INSPECTION_FORM',
  TERRAIN_PARK_INCIDENT_REPORT_FORM: 'TERRAIN_PARK_INCIDENT_REPORT_FORM',
  FEATURE_IMAGES_NOTES_FORM: 'FEATURE_IMAGES_NOTES_FORM',
  PUBLIC_TRAIL_ISSUE_FORM: 'PUBLIC_TRAIL_ISSUE_FORM',
  LIFT_DOWNTIME_LOG_FORM: 'LIFT_DOWNTIME_LOG_FORM',
};

class Form {
  constructor({
    formType,
    resortId,
    name,
    allowFeatureAssociation,
    allowTrailAssociation,
    allowSpatialFeatureAssociation,
    questionSets,
    sports,
    categories,
    tagIds,
    tags,
    folder,
    builderSettings,
    isPrivate,
    hideFromAuthor,
  }) {
    if (!formType) {
      throw new Error('missing "formType" when creating form');
    }
    if (!resortId) {
      throw new Error('missing resportId');
    }
    if (!name) {
      throw new Error('missing name');
    }
    if (!questionSets || !questionSets.length) {
      throw new Error('an array of 1 or more "questionSets" is required when creating a Form');
    }
    if (!questionSets.every((o) => o instanceof QuestionSet)) {
      throw new Error('all "questionSets" must be an instance of QuestionSet');
    }
    this.id = uuidv4();
    this.formType = formType;
    this.resortId = resortId;
    this.name = name;
    this.allowFeatureAssociation = !!allowFeatureAssociation;
    this.allowTrailAssociation = !!allowTrailAssociation;
    this.allowSpatialFeatureAssociation = allowSpatialFeatureAssociation || [];
    this.questionSets = questionSets;
    this.dateCreated = Date.now();
    this.sports = sports;
    this.categories = categories;
    this.tagIds = Array.isArray(tagIds) ? [...tagIds] : [];
    this.tags = Array.isArray(tags) ? [...tags] : undefined;
    // Hydrated by the API from `categories` (ullr-173); read-only, the server
    // ignores it on a write. Absent for a multi-folder form — use `categories`.
    this.folder = folder && folder.id ? { ...folder } : undefined;
    this.builderSettings = builderSettings || {}
    // Carried when the API sent it: a response's frozen privacy is part of
    // what decides who may edit it (ULLR-183 §5.3, P22).
    if (typeof isPrivate === 'boolean') {
      this.isPrivate = isPrivate;
    }
    // Which kind of private (ULLR-183 D25): true = folder Admins only, the
    // author can submit but not see the response afterwards. Only meaningful
    // with isPrivate; the server stores false otherwise. A response freezes it.
    if (typeof hideFromAuthor === 'boolean') {
      this.hideFromAuthor = hideFromAuthor;
    }
  }

  static formType() {
    return undefined;
  }

  static factory(rawObject) {
    const constructedForm = new Form({
      ...rawObject,
      questionSets: rawObject.questionSets.map((qs) => QuestionSet.factory(qs)),
    });
    if (rawObject.id) {
      constructedForm.id = rawObject.id;
    }
    if (rawObject.dateCreated) {
      constructedForm.dateCreated = rawObject.dateCreated;
    }
    return constructedForm;
  }

  applyBuilderSettings() {
    this.applyBuilderSettingsParkLog();
    this.applyBuilderSettingsLiftDowntimeLog();
  }

  applyBuilderSettingsParkLog() {
    if (this.formType === STANDARD_FORM_TYPES.PARK_LOG_FORM) {
      for (const questionSet of this.questionSets) {
        questionSet.builderSettings = {
          ...(questionSet.builderSettings || {}),
          /**
           * The client apps aren't set up to handle complex question types in logs.
           * Neither is the API.
           */
          disableQuestionTypes: [
            ImagesQuestion.questionType(),
            GeographicLocationQuestion.questionType(),
            SelectByFeatureQuestion.questionType(),
            SignatureQuestion.questionType(),
            MarkdownQuestion.questionType(),
          ],
        };
        for (const question of questionSet.questions) {
          question.builderSettings = {};
          // restrict editing on `actions` question
          if (question.field === 'actions') {
            question.builderSettings = {
              preventRemove: true,
              preventDuplicate: true,
              lockRequired: true,
              lockPrompt: true,
            };
            questionSet.builderSettings = {
              ...(questionSet.builderSettings || {}),
              preventRemove: true,
              preventDuplicate: true,
            };
            // restrict `open` and `close` actions
            for (const selectOption of question.options) {
              if (selectOption.value.toLowerCase() === 'open') {
                selectOption.builderSettings = {
                  preventRemove: true,
                  lockValue: true,
                }
              }
              if (selectOption.value.toLowerCase() === 'close') {
                selectOption.builderSettings = {
                  preventRemove: true,
                  lockValue: true,
                }
              }
            }
          }
          // restrict editing on `timestamp` question
          if (question.field === 'timestamp') {
            question.builderSettings = {
              preventRemove: true,
              preventDuplicate: true,
              lockRequired: true,
              lockPrompt: true,
            };
            questionSet.builderSettings = {
              ...(questionSet.builderSettings || {}),
              preventRemove: true,
              preventDuplicate: true,
            };
          }
        }
      }
    }
  }

  applyBuilderSettingsLiftDowntimeLog() {
    if (this.formType === STANDARD_FORM_TYPES.LIFT_DOWNTIME_LOG_FORM) {
      for (const questionSet of this.questionSets) {
        questionSet.builderSettings = {
          ...(questionSet.builderSettings || {}),
        };
        for (const question of questionSet.questions) {
          question.builderSettings = {};
          // restrict editing on `actions` question
          if (question.field === 'downtime cause') {
            question.builderSettings = {
              preventRemove: true,
              preventDuplicate: true,
              lockRequired: true,
              lockPrompt: true,
            };
            questionSet.builderSettings = {
              ...(questionSet.builderSettings || {}),
              preventRemove: true,
              preventDuplicate: true,
            };
          }
          // restrict editing on `start timestamp` question
          if (question.field === 'start timestamp') {
            question.builderSettings = {
              preventRemove: true,
              preventDuplicate: true,
              lockRequired: true,
              lockPrompt: true,
            };
            questionSet.builderSettings = {
              ...(questionSet.builderSettings || {}),
              preventRemove: true,
              preventDuplicate: true,
            };
          }
          // restrict editing on `end timestamp` question
          if (question.field === 'end timestamp') {
            question.builderSettings = {
              preventRemove: true,
              preventDuplicate: true,
              lockRequired: true,
              lockPrompt: true,
            };
            questionSet.builderSettings = {
              ...(questionSet.builderSettings || {}),
              preventRemove: true,
              preventDuplicate: true,
            };
          }
        }
      }
    }
  }
}

export {
  Form,
  STANDARD_FORM_TYPES,
};
